<script setup>
import AppWell from '../Shared/AppWell.vue';
import { computed, ref } from 'vue';
import deburr from 'lodash/deburr';
import AppCard from '../Shared/AppCard.vue';
import BadgePill from './BadgePill.vue';
import { useVModel } from '@vueuse/core';
import SelectAllNoneButtons from './SelectAllNoneButtons.vue';

const props = defineProps({
    blocks: {
        type: Array,
        required: true,
    },
    modelValue: {
        type: Array,
        required: false,
        default: () => [],
    },
});

const emit = defineEmits(['update:modelValue']);

const selectedTags = useVModel(props, 'modelValue', emit);

const blockSearch = ref('');

const filteredBlocks = computed(() => {
    const search = deburr(blockSearch.value.toLowerCase());

    if (search === '') {
        return props.blocks;
    }
    return props.blocks
        .map((b) => {
            return {
                ...b,
                tags: b.tags.filter(
                    (t) => deburr(t.name.toLowerCase()).includes(search) || selectedTags.value.includes(t.id),
                ),
            };
        })
        .filter((b) => b.tags.length > 0);
});

const maxCount = computed(() => {
    return props.blocks.flatMap((b) => b.tags).length;
});

const selectAll = () => {
    selectedTags.value = props.blocks.flatMap((b) => b.tags).map((t) => t.id);
};

defineOptions({
    name: 'QuestionTagSelectorByBlock',
});
</script>

<template>
    <AppWell v-if="blocks.length" class="mt-3">
        <div class="">
            <label class="block text-sm font-medium leading-5 text-gray-700">{{ $t('messages.search') }}</label>
            <div class="mt-2 relative rounded-md shadow-sm">
                <input
                    v-model="blockSearch"
                    type="text"
                    :placeholder="$tc('messages.tags', 2)"
                    class="pr-10 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm w-full"
                />
                <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <svg class="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path
                            fill-rule="evenodd"
                            d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                            clip-rule="evenodd"
                        ></path>
                    </svg>
                </div>
            </div>
        </div>

        <div>
            <SelectAllNoneButtons
                :max-count="maxCount"
                :selected-count="selectedTags.length"
                @select-all="selectAll()"
                @select-none="selectedTags = []"
                class="flex md:w-12"
            />
        </div>

        <AppWell v-if="blocks.length" class="mt-3">
            <div class="md:grid md:grid-cols-12 md:gap-6" v-auto-animate>
                <AppCard v-for="block in filteredBlocks" :key="block.id" class="sm:col-span-6 lg:col-span-3">
                    <div class="font-medium text-gray-700">
                        {{ block.name }}
                    </div>
                    <div class="mt-3 flex flex-col gap-2" v-auto-animate>
                        <label v-for="tag in block.tags" :key="tag.id">
                            <BadgePill :color="selectedTags.includes(tag.id) ? 'indigo' : 'gray'">
                                <input
                                    type="checkbox"
                                    name="tag_ids[]"
                                    :id="tag.id"
                                    v-model="selectedTags"
                                    :value="tag.id"
                                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                />
                                <span class="ml-1">{{ tag.name }}</span>
                            </BadgePill>
                        </label>
                    </div>
                </AppCard>
            </div>
        </AppWell>
    </AppWell>
</template>
